import React, { useState, useEffect, useRef } from "react";
import { Device } from "@twilio/voice-sdk";
import AngelWrapper from "./angel3";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Link } from "react-router-dom";
import {
  Phone,
  PhoneOff,
  ArrowLeft,
  User,
  FileText,
  Volume2,
  VolumeX,
} from "lucide-react";
import "./Lexburner.css";
import MyStuff from "./MyStuff";
import { useAuth } from "../user/AuthContext";

import LexLoginSignupPopup from '../miniapps/lex-loginsignup-popup/lex-loginsignup-popup';
import '../miniapps/lex-loginsignup-popup/lex-loginsignup-popup.css';


const API = process.env.REACT_APP_API_URL || "https://api.lextrackai.com";

export default function Lexburner({ summaryItems }) {
  const { user, loginWithGoogle } = useAuth();

  const connectionRef = useRef(null);
  const gainNodeRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState("loading");
  const [phone, setPhone] = useState("");
  const [digits, setDigits] = useState("");
  const [active, setActive] = useState("dialer");
  const [duration, setDuration] = useState("00:00");
  const [volume, setVolume] = useState(0.15);
  const [muted, setMuted] = useState(false);
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  const tabs = [];
  const dialKeys = ["1","2","3","4","5","6","7","8","9","0","*","#"];

  const dialAudio = useRef(null);
  useEffect(() => { dialAudio.current = new Audio("/sounds/dialkey3.mp3"); }, []);

  useEffect(() => {
    fetch(`${API}/api/token?identity=lexburner`)
      .then(r => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then(({ token }) => {
        const dev = new Device(token, {
          codecPreferences: ["opus", "pcmu"],
          region: "us1",
          logLevel: "debug",
        });
        dev.register();

        dev.on("registered",        () => setStatus("ready"));
        dev.on("registrationFailed",() => setStatus("error"));
        dev.on("error", e => { console.error("Device error", e); setStatus("error"); });

        dev.on("incoming", incoming => {
          incoming.accept().then(() => wireCallEvents(incoming, dev));
        });

        setDevice(dev);
      })
      .catch(err => { console.error(err); setStatus("error"); });
  }, []);

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const diff = Date.now() - startTimeRef.current;
      setDuration(
        `${String(Math.floor(diff / 60000)).padStart(2,"0")}:${String(Math.floor(diff / 1000) % 60).padStart(2,"0")}`
      );
    }, 1000);
  };
  const stopTimer = () => { clearInterval(timerRef.current); setDuration("00:00"); };

  function buildAudioGraph(call, dev) {
    const ctx = dev.audioContext;
    if (!ctx) return;

    const gain = ctx.createGain();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    gain.gain.value = muted ? 0 : volume;

    const remote = call.getRemoteStream?.() || call.mediaStream;
    if (remote) ctx.createMediaStreamSource(remote).connect(gain);

    const local = call.getInputStream?.();
    if (local) ctx.createMediaStreamSource(local).connect(gain);

    gain.connect(analyser).connect(ctx.destination);
    gainNodeRef.current = { gain, analyser };
  }

  const cleanupAudioGraph = () => {
    if (gainNodeRef.current) {
      try { gainNodeRef.current.gain.disconnect(); } catch {}
      gainNodeRef.current = null;
    }
  };

  const wireCallEvents = (call, dev) => {
    connectionRef.current = call;
    setStatus("ringing");

    call.on("accept", () => {
      setStatus("in-call");
      startTimer();
      buildAudioGraph(call, dev);
    });

    call.on("disconnect", () => {
      stopTimer();
      cleanupAudioGraph();
      connectionRef.current = null;
      setStatus("ready");
      setDigits("");
      setPhone("");
    });

    call.on("error", () => setStatus("error"));
  };

  const handleDigit = k => {
    dialAudio.current?.play();
    setDigits(d => d + k);
    setPhone("+" + (digits + k).replace(/\D/g, ""));
  };
  const handleDelete = () => {
    const nxt = digits.slice(0, -1);
    setDigits(nxt);
    setPhone(nxt ? "+" + nxt.replace(/\D/g, "") : "");
  };

  const handleCall = async () => {
    if (!user) {
      setLoginPromptVisible(true);
      return;
    }
    if (!device || status !== "ready") return alert("Device not ready");
    const ctx = device.audioContext;
    if (ctx?.state === "suspended") await ctx.resume().catch(() => {});
    const call = await device.connect({ params: { To: phone } });
    wireCallEvents(call, device);
  };

  const handleHangup = () => connectionRef.current?.disconnect();

  const onVol = v => {
    const val = parseFloat(v);
    setVolume(val);
    if (!muted && gainNodeRef.current) gainNodeRef.current.gain.gain.value = val;
  };

  const toggleMute = () =>
    setMuted(m => {
      const nxt = !m;
      if (gainNodeRef.current) gainNodeRef.current.gain.gain.value = nxt ? 0 : volume;
      return nxt;
    });

  useEffect(() => {
    if (summaryItems?.length) {
      window.speechSynthesis.speak(
        new SpeechSynthesisUtterance(summaryItems.join(", "))
      );
    }
  }, [summaryItems]);

  return (
    <AngelWrapper>
      <Link to="/app" className="appstore-back">← Back</Link>
      <div className="lexburner">
      

        {active === "dialer" && (
          <div className="dialer-ui">
            <div className="dialer-header">
              <PhoneInput
                international
                defaultCountry="US"
                placeholder="Enter number"
                value={phone}
                onChange={setPhone}
                className="phone-input"
              />
              <button className="delete-btn-1" onClick={handleDelete}>
                <ArrowLeft size={20} style={{ stroke: "#fff", strokeWidth: 2, fill: "none", opacity: 1 }} />
              </button>
              {status === "in-call" ? (
                <button className="dialer‑action‑call" onClick={handleHangup}>
                  <PhoneOff size={20} strokeWidth={2}/>
                </button>
              ) : (
                <button className="dialer‑action‑call" onClick={handleCall} disabled={status !== "ready"}>
                  <Phone size={20} />
                </button>
              )}
            </div>
            <div className="call-status">
              {status === "in-call" ? `In Call – ${duration}` : `Status: ${status}`}
            </div>
            {status !== "in-call" && (
              <div className="dialer-grid">
                {dialKeys.map(k => (
                  <button key={k} className="dialer-key" onClick={() => handleDigit(k)}>
                    {k}
                  </button>
                ))}
              </div>
            )}
            {status === "in-call" && (
              <div className="in-call-controls">
                <button className="dialer-action-call" onClick={toggleMute}>
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={muted ? 0 : volume}
                  onChange={e => onVol(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

      

{loginPromptVisible && !user && (
  <LexLoginSignupPopup
    loginPromptVisible={loginPromptVisible}
    setLoginPromptVisible={setLoginPromptVisible}
  />
)}


      </div>
    </AngelWrapper>
  );
}
